import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { fa } from '@/i18n/fa';

/** Merge Tailwind class names, resolving conflicts (last wins). */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Avatar initials placeholder (1-2 chars) used when a person has no photo. */
export function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fa.common.unknownInitial;
  if (parts.length === 1) return parts[0].slice(0, 2);
  return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`;
}

/** Human-readable file size, e.g. 2.3 MB. */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${exponent === 0 ? value : value.toFixed(1)} ${units[exponent]}`;
}

/**
 * Derives a pre-suggested document title from a filename: strips the
 * extension, replaces separators with spaces, and tidies whitespace/casing.
 * Falls back to a generic label for camera captures (`capture-<ts>.jpg`) or
 * unusable names.
 */
export function titleFromFilename(filename: string): string {
  const withoutExt = filename.replace(/\.[^./\\]+$/, '');
  const tidied = withoutExt
    .replace(/^capture-\d+$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!tidied) return '';
  return tidied.charAt(0).toLocaleUpperCase('fa') + tidied.slice(1);
}
